import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from '../Modal';
import LoadingButton from '../LoadingButton';
import StatusLabel from '../StatusLabel';
import { invokeApi } from '../../../lib/invokeApi';
import { getRecentErrors } from '../../../lib/consoleErrorBuffer';
import { captureScreenshot } from '../../../lib/captureScreenshot';

interface ReportBugResponse {
  success: boolean;
  ticketId?: string;
  duplicate?: boolean;
  error?: string;
}

interface BugReporterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Stable for the lifetime of one report attempt so a resubmit dedupes server-side. */
  reportId: string;
}

const BugReporterPanel: React.FC<BugReporterPanelProps> = ({ isOpen, onClose, reportId }) => {
  const location = useLocation();
  const [description, setDescription] = useState('');
  const [includeConsoleErrors, setIncludeConsoleErrors] = useState(true);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setDescription('');
    setIncludeConsoleErrors(true);
    setScreenshot(null);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAttachScreenshot = async () => {
    setCapturingScreenshot(true);
    try {
      const dataUrl = await captureScreenshot();
      setScreenshot(dataUrl);
    } finally {
      setCapturingScreenshot(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || submitting) return;

    setSubmitting(true);
    setError(null);

    const { data, error: submitError } = await invokeApi<ReportBugResponse>('report-bug', {
      description,
      route: location.pathname,
      consoleErrors: includeConsoleErrors ? getRecentErrors() : [],
      hasScreenshot: screenshot !== null,
      reportId,
    });

    setSubmitting(false);

    if (submitError || !data?.success) {
      setError(submitError?.message || data?.error || 'Failed to submit bug report.');
      return;
    }

    setSuccess(true);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Report a bug" size="md">
      {success ? (
        <div className="space-y-4">
          <StatusLabel tone="positive">Report submitted — thank you.</StatusLabel>
          <p className="text-sm text-ink-700">
            The engineering team will pick this up from the ticket queue. No further action needed.
          </p>
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="bug-description" className="block text-sm font-medium text-ink-900 mb-1">
              What happened?
            </label>
            <textarea
              id="bug-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="w-full border border-stone-200 rounded p-2 text-sm"
              placeholder="Describe what you were doing and what went wrong..."
              disabled={submitting}
              autoFocus
            />
            <p className="text-xs text-ink-400 mt-1">Route: {location.pathname}</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={includeConsoleErrors}
              onChange={(e) => setIncludeConsoleErrors(e.target.checked)}
              disabled={submitting}
            />
            Include recent console errors
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={handleAttachScreenshot}
              disabled={capturingScreenshot || submitting}
            >
              {capturingScreenshot ? 'Capturing...' : screenshot ? 'Retake screenshot' : 'Attach screenshot'}
            </button>
            {screenshot && (
              <img
                src={screenshot}
                alt="Screenshot preview"
                className="h-12 border border-stone-200 rounded"
              />
            )}
          </div>

          {error && <StatusLabel tone="critical">{error}</StatusLabel>}

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <LoadingButton
              loading={submitting}
              loadingText="Submitting..."
              disabled={!description.trim()}
              className="w-auto"
            >
              Submit
            </LoadingButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default BugReporterPanel;
